import * as React from "react"
import { useRouter } from "components/Router"
import { useTranslator } from "components/Translator"
import { client } from "api/client"

const LikesContext = React.createContext()

export const LikesProvider = ({ type, ids, children }) => {
  const [likes, setLikes] = React.useState({})
  const router = useRouter()
  const translator = useTranslator()

  const fetchLikes = React.useCallback(
    async (ids) => {
      const url = new URL(
        router.generate(
          "app_user_like_exist",
          {
            type,
          },
          true,
        ),
      )

      ids.forEach((id) => url.searchParams.append("ids[]", id))

      try {
        const response = await client.get(url.toString())

        setLikes(
          ids.reduce((acc, id) => {
            acc[id] = response.data.data.includes(id)
            return acc
          }, {}),
        )
      } catch (err) {
        console.error("Error while fetching likes:")

        if (err.response?.data?.errors) {
          console.error(err.response.data.errors)
        }

        console.error(err)
      }
    },
    [router, type],
  )

  const isLiked = (id) => {
    return likes[id] || false
  }

  const like = async (id) => {
    if (isLiked(id)) {
      return
    }

    setLikes((prevLikes) => ({
      ...prevLikes,
      [id]: true,
    }))

    const url = new URL(
      router.generate(
        "app_user_like_add",
        {
          type,
          id,
        },
        true,
      ),
    )

    try {
      await client.post(url)
    } catch (err) {
      console.error("Error while adding like:")

      if (err.response?.data?.errors) {
        console.error(err.response.data.errors)
      }

      console.error(err)
    }
  }

  const unlike = async (id) => {
    if (!isLiked(id)) {
      return
    }

    setLikes((prevLikes) => ({
      ...prevLikes,
      [id]: false,
    }))

    const url = new URL(
      router.generate(
        "app_user_like_delete",
        {
          _locale: translator.locale,
          type,
          id,
        },
        true,
      ),
    )

    try {
      await client.delete(url)
    } catch (err) {
      console.error("Error while deleting like:")

      if (err.response?.data?.errors) {
        console.error(err.response.data.errors)
      }

      console.error(err)
    }
  }

  const toggleLike = (id) => {
    return isLiked(id) ? unlike(id) : like(id)
  }

  React.useEffect(() => {
    fetchLikes(ids)
  }, [fetchLikes, ids, type])

  const value = { isLiked, like, unlike, toggleLike }

  return <LikesContext.Provider value={value}>{children}</LikesContext.Provider>
}

export const useLikes = () => {
  const context = React.useContext(LikesContext)

  if (!context) {
    throw new Error("useLikes must be used in a LikesProvider")
  }

  return context
}
